import { API_URL } from '../services/api';

// API origin (no trailing "/api" suffix) is where uploaded files are served
// from. In production nginx proxies /uploads/ on the same host as the API.
const getApiOrigin = (): string =>
  API_URL.replace(/\/api\/?$/, '').replace(/\/+$/, '');

// Small stable hash so cache-busting never changes between renders while the
// avatar URL stays the same (uploaded filenames are content-hashed, so a new
// upload always produces a new URL).
const hashString = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

/**
 * Canonical avatar URL helper.
 *
 * Root-cause fix for "avatar never displays": early backend builds stored
 * uploads as `http://localhost:3000/uploads/<hash>.webp` (BASE_URL was unset,
 * see LocalStorageProvider.getPublicUrl default) or as relative
 * `uploads/<hash>.webp` paths. Those URLs are permanently stored in the user
 * rows, so every profile load referenced an unreachable host. Independent of
 * backend behaviour we normalize those legacy values here:
 *
 *   1. Relative paths → absolute URL against the API origin.
 *   2. localhost / 127.0.0.1 hosts → rewritten to the API origin.
 *   3. data: URLs (client-side previews) → passed through untouched.
 *   4. A stable cache-buster derived from the URL itself (never Date.now(),
 *      which produced a brand-new URL on every render and re-fetched the
 *      image constantly).
 *
 * New uploads already return absolute URLs (BASE_URL or request-derived),
 * so this function is a safety net for stored legacy data — it never rewrites
 * valid remote URLs.
 */
const normalizeUrl = (url?: string | null): string => {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // Client-side image previews (FileReader data URLs) need no normalization.
  if (trimmed.startsWith('data:')) return trimmed;

  const origin = getApiOrigin();

  let normalized = trimmed;

  // 1. Relative path (e.g. "uploads/<hash>.webp") → absolute on the API origin.
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `${origin}/${normalized.replace(/^\/+/, '')}`;
  }

  // 2. Legacy localhost / 127.0.0.1 URLs stored by early builds → swap host.
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(normalized)) {
    normalized = normalized.replace(/^https?:\/\/[^/]+/i, origin);
  }

  // 3. Stable cache-buster.
  if (!/[?&]v=/.test(normalized)) {
    const separator = normalized.includes('?') ? '&' : '?';
    normalized = `${normalized}${separator}v=${hashString(normalized)}`;
  }

  return normalized;
};

export const getAvatarUrl = normalizeUrl;

/** Canonical URL for artwork/order images — same legacy normalization as avatars. */
export const getImageUrl = (url?: string | null): string => normalizeUrl(url);